import { useEffect, useRef } from 'react';

/**
 * Auto-dismiss a transient banner/notification after a delay.
 *
 * Pass the current value (e.g. an error string or a result object) and a
 * callback that clears it. The timer (re)starts whenever `value` becomes a new
 * truthy value and is cancelled if the value clears or the component unmounts —
 * so a fresh error resets the countdown and a manual dismiss won't double-fire.
 *
 * @param value      the banner's current value; `null`/`undefined`/`false` = hidden
 * @param onDismiss  called once when the delay elapses (typically clears `value`)
 * @param delayMs    time to wait before dismissing (default 5000ms)
 */
export function useAutoDismiss(
  value: unknown,
  onDismiss: () => void,
  delayMs = 5000,
): void {
  // Keep the latest callback without restarting the timer on every render.
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  useEffect(() => {
    if (value === null || value === undefined || value === false) return;

    const timer = setTimeout(() => onDismissRef.current(), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
}
